/**
 * 租户端布局组件
 * 为租户用户提供专属的管理界面布局
 * 支持业务模式和管理模式切换
 * 支持响应式布局，移动端使用抽屉式菜单
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Badge, Space, Typography, Tag, Drawer, ConfigProvider } from 'antd';
import { message } from '@/utils/antdStatic';
import type { MenuProps } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  ShoppingOutlined,
  ToolOutlined,
  MenuOutlined,
  CloseOutlined,
  ShoppingCartOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserType } from '@/hooks/useUserType';
import { useTenantTheme } from '@/hooks/useTenantTheme';
import { resolveUiSkin } from '@/theme/forest';
import { openMallSecure, resolveMallUrl } from '@/utils/authSync';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import TenantSwitcher from '@/components/TenantSwitcher';
import OrganizationSwitcher from '@/components/OrganizationSwitcher';
// REQ-20260508-0001 #10182: 门户切换组件
import PortalSwitcher from '@/components/PortalSwitcher';
import { LanguageSwitcher, AppVersion } from '@/components';
import { useAdminMode, AdminModeSwitch, AdminModeBanner, AdminModeProvider, useBranding } from '@/modules/foundation';
import { cartApi } from '@/api/modules/cart';
import NotificationPopover from '@/modules/foundation/pages/MessageCenter/NotificationPopover';
import HeaderCreditSummary from './HeaderCreditSummary';
import AnnouncementPopup from '@/modules/foundation/pages/MessageCenter/AnnouncementPopup';
import AnnouncementBanner from '@/modules/foundation/pages/MessageCenter/AnnouncementBanner';
import { useMenuItems } from './useMenuItems';
import { WorkMode } from './workMode';
import { getHomePath } from './homeMenu';
import { clearPageTabs, usePageTabs } from './pageTabs/usePageTabs';
import { PageTabsBar, PageTabsOutlet } from './pageTabs/PageTabs';
import './index.less';

export { WorkMode } from './workMode';

// 响应式断点
const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const TenantLayoutInner: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const { logout } = useAuth();
  const { userInfo, tenantName } = useUserType();
  const { antdTheme, themeConfig } = useTenantTheme();
  const { branding } = useBranding();
  const {
    isInAdminMode,
    hasPermission,
    saveCurrentLocation,
    getBusinessLocation,
    getAdminLocation,
    setOnTimeoutCallback,
    checkStatus,
  } = useAdminMode();

  // 进入租户布局时检查管理模式状态
  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // 加载购物车数量
  useEffect(() => {
    cartApi.getCartCount()
      .then(res => {
        if (res?.success && res?.data?.count !== undefined) {
          setCartCount(res.data.count);
        } else {
          setCartCount(0);
        }
      })
      .catch(err => {
        // 购物车功能可能未启用，静默处理
        console.warn('Cart count unavailable:', err?.message || 'API not available');
        setCartCount(0);
      });
  }, [location.pathname]);

  // 森林绿 UI 灰度开关（响应租户 extraConfig / 本地覆盖），决定侧栏/菜单明暗
  const isForest = resolveUiSkin(themeConfig) === 'forest';
  const menuTheme = isForest ? 'light' : 'dark';

  // 工作模式基于管理模式状态
  const workMode = isInAdminMode ? WorkMode.ADMIN : WorkMode.BUSINESS;

  // 响应式检测
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < MOBILE_BREAKPOINT);
      setIsTablet(width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT);
      // 平板自动折叠侧边栏
      if (width < TABLET_BREAKPOINT && width >= MOBILE_BREAKPOINT) {
        setCollapsed(true);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // 路由变化时关闭移动端抽屉
  useEffect(() => {
    if (isMobile) {
      setDrawerVisible(false);
    }
  }, [location.pathname, isMobile]);

  // 路由变化时自动保存当前位置
  useEffect(() => {
    // 保存当前路径到对应模式的位置记忆
    if (location.pathname.startsWith('/tenant')) {
      saveCurrentLocation(location.pathname);
    }
  }, [location.pathname, saveCurrentLocation]);

  // 设置超时退出回调 - 跳转到业务模式保存的位置
  useEffect(() => {
    setOnTimeoutCallback(() => {
      const savedBusinessLocation = getBusinessLocation();
      navigate(savedBusinessLocation || '/tenant/dashboard');
    });
  }, [setOnTimeoutCallback, getBusinessLocation, navigate]);

  // 菜单项 - 从后端动态获取
  const { menuItems, getOpenKeys } = useMenuItems(workMode);

  // 多标签页（#1249），手机端不启用
  const pageTabs = usePageTabs({ workMode, menuItems });

  // 当前选中的菜单
  const selectedKeys = useMemo(() => [location.pathname], [location.pathname]);

  // 展开的菜单 - 支持用户手动展开/折叠 + 路径变化自动匹配
  const [currentOpenKeys, setCurrentOpenKeys] = useState<string[]>([]);

  // 路径变化时自动展开对应的父级菜单
  useEffect(() => {
    const matched = getOpenKeys(location.pathname);
    if (matched.length > 0) {
      setCurrentOpenKeys(prev => {
        const merged = new Set([...prev, ...matched]);
        return Array.from(merged);
      });
    }
  }, [location.pathname, getOpenKeys]);

  // 打开采购商城（通过 postMessage 安全传递 token，不再拼 URL）
  const openMall = () => {
    const mallUrl = resolveMallUrl();
    if (!mallUrl) {
      message.error('采购商城地址未配置，请联系管理员');
      return;
    }
    // ?force=mall 告诉 mall 的 PortalEntryRouter 跳过 portal-suggest，
    // 因为这是管理端菜单的主动跳转，不需要再做身份分流。
    const url = new URL(mallUrl);
    url.searchParams.set('force', 'mall');
    openMallSecure(url.toString());
  };

  // 菜单点击处理
  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'external:mall') {
      openMall();
      return;
    }
    if (isMobile) {
      navigate(key);
    } else {
      pageTabs.openMenu(key);
    }
  };

  // 用户下拉菜单 (REQ-20260508-0001 #10182: 双 portal 用户在顶部插入 PortalSwitcher)
  const userMenuItems: MenuProps['items'] = [
    // 仅当 user.available_portals 含 admin+mall 时 PortalSwitcher 内部会渲染；否则返 null
    {
      key: 'portal-switcher',
      label: <PortalSwitcher currentPortal="admin" />,
      style: {
        cursor: 'default',
        padding: 0,
        margin: 0,
        height: 'auto',
      },
    },
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('header.profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('header.settings'),
    },
    {
      key: 'guide',
      icon: <BookOutlined />,
      label: t('header.guide'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('header.logout'),
      danger: true,
    },
  ];

  // 用户菜单点击
  const handleUserMenuClick: MenuProps['onClick'] = async ({ key }) => {
    if (key === 'portal-switcher') {
      openMall();
    } else if (key === 'logout') {
      await logout();
      clearPageTabs();
      navigate('/login', { replace: true });
    } else if (key === 'profile') {
      navigate('/tenant/profile');
    } else if (key === 'settings') {
      navigate('/tenant/account/settings');
    } else if (key === 'guide') {
      // 以新标签页打开操作手册静态页面
      window.open('/guide', '_blank', 'noopener,noreferrer');
    }
  };

  // 使用后端返回的 tenantName，确保与平台端设置一致
  const displayTenantName = tenantName || t('common.enterprise');

  // 菜单触发器点击处理
  const handleTriggerClick = () => {
    if (isMobile) {
      setDrawerVisible(true);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const homePath = getHomePath(workMode);

  const goHome = () => {
    navigate(homePath);
    if (isMobile) setDrawerVisible(false);
  };

  const handleLogoKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      goHome();
    }
  };

  // 渲染菜单内容
  const renderMenuContent = (isDrawer = false) => (
    <div className="sider-content">
      <div
        className="tenant-logo"
        role="link"
        tabIndex={0}
        aria-label={t('menu.home')}
        title={t('menu.home')}
        onClick={goHome}
        onKeyDown={handleLogoKeyDown}
      >
        {branding.logo_url ? (
          <img
            src={branding.logo_url}
            alt={branding.system_name || displayTenantName}
            className="logo-img"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        ) : null}
        <span className="logo-text">{branding.system_name || displayTenantName}</span>
      </div>
      <div className="menu-wrapper">
        <Menu
          theme={menuTheme}
          mode="inline"
          selectedKeys={selectedKeys}
          openKeys={currentOpenKeys}
          onOpenChange={setCurrentOpenKeys}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </div>
      <AppVersion collapsed={!isDrawer && collapsed} theme={menuTheme} />
    </div>
  );

  // 计算布局类名
  const layoutClassName = useMemo(() => {
    const classes = ['tenant-layout'];
    if (collapsed) classes.push('collapsed');
    if (isMobile) classes.push('mobile');
    if (isTablet) classes.push('tablet');
    if (isInAdminMode) classes.push('admin-mode-active');
    return classes.join(' ');
  }, [collapsed, isMobile, isTablet, isInAdminMode]);

  return (
    <ConfigProvider theme={antdTheme}>
      {/* 管理模式顶部提示条 */}
      <AdminModeBanner
        compact={isMobile}
        onExit={() => {
          // 退出时恢复到业务模式上次访问位置
          const savedBusinessLocation = getBusinessLocation();
          navigate(savedBusinessLocation || '/tenant/dashboard');
        }}
      />
      <Layout className={layoutClassName}>
        {/* 移动端使用 Drawer */}
        {isMobile ? (
          <Drawer
            placement="left"
            open={drawerVisible}
            onClose={() => setDrawerVisible(false)}
            width={260}
            className={`tenant-drawer${isInAdminMode ? ' admin-mode-drawer' : ''}`}
            styles={{
              body: {
                padding: 0,
                background: isForest
                  ? 'var(--fg-card, #fff)'
                  : isInAdminMode
                    ? 'linear-gradient(180deg, #1a0a2e 0%, #001529 100%)'
                    : '#001529'
              },
              header: { display: 'none' },
            }}
            closeIcon={<CloseOutlined style={{ color: '#fff' }} />}
          >
            <div className="drawer-close-btn" onClick={() => setDrawerVisible(false)}>
              <CloseOutlined />
            </div>
            {renderMenuContent(true)}
          </Drawer>
        ) : (
          <Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
            theme={menuTheme}
            className="tenant-sider"
            width={220}
            collapsedWidth={80}
          >
            {renderMenuContent(false)}
          </Sider>
        )}
        <Layout>
          <Header className="tenant-header">
            <div className="header-left">
              {React.createElement(
                isMobile ? MenuOutlined : (collapsed ? MenuUnfoldOutlined : MenuFoldOutlined),
                {
                  className: 'trigger',
                  onClick: handleTriggerClick,
                }
              )}
              {/* 移动端显示租户名称 */}
              {isMobile && (
                <span className="mobile-logo-text">{branding.system_name || displayTenantName}</span>
              )}
              {/* 当前模式标签 - 移动端隐藏 */}
              {!isMobile && (
                <Tag
                  color={workMode === WorkMode.ADMIN ? 'purple' : 'blue'}
                  icon={workMode === WorkMode.ADMIN ? <ToolOutlined /> : <ShoppingOutlined />}
                  style={{ marginLeft: 16 }}
                >
                  {workMode === WorkMode.ADMIN ? t('menu.adminMode') : t('menu.businessMode')}
                </Tag>
              )}
            </div>
            <div className="header-right">
              <Space size={isMobile ? 'small' : 'middle'}>
                {/* 管理模式切换开关 - 只有有权限的用户可见 */}
                {hasPermission && (
                  <AdminModeSwitch
                    compact={isMobile}
                    onEnterSuccess={() => {
                      // 恢复到管理模式上次访问位置，或默认管理首页
                      const savedAdminLocation = getAdminLocation();
                      navigate(savedAdminLocation || '/tenant/admin/dashboard');
                    }}
                    onExitSuccess={() => {
                      // 恢复到业务模式上次访问位置，或默认业务首页
                      const savedBusinessLocation = getBusinessLocation();
                      navigate(savedBusinessLocation || '/tenant/dashboard');
                    }}
                  />
                )}
                {/* 语言切换器 */}
                <LanguageSwitcher type="icon" showLabel={!isMobile} />
                {/* 组织切换器 */}
                <OrganizationSwitcher compact={isMobile} />
                {/* 租户切换器 */}
                <TenantSwitcher compact={isMobile} />
                <Badge count={cartCount} size="small">
                  <ShoppingCartOutlined
                    className="header-icon"
                    onClick={openMall}
                    style={{ cursor: 'pointer' }}
                  />
                </Badge>
                <HeaderCreditSummary key={`${userInfo?.id}:${userInfo?.tenant_id}`} tenantId={userInfo?.tenant_id} compact={isMobile} />
                <NotificationPopover />
                <Dropdown
                  menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
                  placement="bottomRight"
                >
                  <Space className="user-info" style={{ cursor: 'pointer' }}>
                    <Avatar
                      src={userInfo?.avatar}
                      icon={!userInfo?.avatar && <UserOutlined />}
                      size={isMobile ? 'small' : 'default'}
                    />
                    {!isMobile && <Text>{userInfo?.nickname || userInfo?.username || t('common.user')}</Text>}
                  </Space>
                </Dropdown>
              </Space>
            </div>
          </Header>
          {!isMobile && (
            <PageTabsBar
              tabs={pageTabs.tabs}
              currentKey={pageTabs.currentKey}
              onSelect={pageTabs.selectTab}
              onRemove={pageTabs.removeTab}
              onRefresh={pageTabs.refreshTab}
              onRemoveAll={pageTabs.removeAllTabs}
            />
          )}
          <Content className="tenant-content">
            <AnnouncementBanner />
            {isMobile ? (
              <Outlet />
            ) : (
              <PageTabsOutlet
                key={workMode}
                currentKey={pageTabs.currentKey}
                tabs={pageTabs.tabs}
                versions={pageTabs.versions}
                onBack={pageTabs.goBack}
              />
            )}
          </Content>
        </Layout>

        {/* 公告弹窗 */}
        <AnnouncementPopup />
      </Layout>
    </ConfigProvider>
  );
};

/**
 * TenantLayout wraps the inner layout with AdminModeProvider.
 * RouterProvider (createBrowserRouter) creates an isolated React context boundary,
 * so providers placed above RouterProvider in App.tsx are not accessible here.
 */
const TenantLayout: React.FC = () => (
  <AdminModeProvider>
    <TenantLayoutInner />
  </AdminModeProvider>
);

export default TenantLayout;
